import React, { useState, useEffect } from 'react';
import { Calendar, Save, AlertTriangle, AlertCircle, Lock, Pencil, CalendarClock, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { api } from '../services/api';
import { useFeatures } from '../contexts/FeaturesContext';
import { Button } from './ui/button';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import EnTeteSection from './parametrage/EnTeteSection';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import { Toast } from './ui/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';

/**
 * Paramétrage du calendrier annuel.
 *
 * ----------------------------------------------------------------------------
 * Deux choses distinctes sur un même écran
 * ----------------------------------------------------------------------------
 * 1. Les BORNES DE L'ANNÉE (`annees_scolaires.date_debut` / `date_fin`).
 *    Elles servent à la génération automatique des emplois du temps, et à rien
 *    d'autre. C'est le seul endroit du système où on peut les corriger.
 *
 * 2. Le CALENDRIER DE PAIE (`mois_scolaires`). C'est lui qui décide à quel mois
 *    appartiennent un pointage ou un paiement — voir CALENDRIER-DE-PAIE.md.
 *
 * Les deux ne coïncident pas, et ce n'est pas une erreur : csap1 commence son
 * année le 6 octobre alors que son mois « Octobre » commence le 1er. Le CIK
 * refuse d'ailleurs explicitement d'imposer que les mois tiennent dans les
 * bornes de l'année, pour ce cas précis.
 *
 * ----------------------------------------------------------------------------
 * D'où viennent les mois, et qui peut les corriger
 * ----------------------------------------------------------------------------
 * Ils ne sont pas créés ici. Le CIK les pose à l'ouverture de l'école, la
 * clôture annuelle les reprend décalés d'un an pour l'année suivante.
 *
 * Cette page les CORRIGE, tant qu'ils ne sont pas consommés. Un mois dont
 * l'état des salaires est validé, ou dont des salaires sont déjà payés, est en
 * lecture seule : déplacer sa frontière changerait le mois de rattachement
 * d'opérations déjà passées. Le backend reconstrôle ce verrou dans sa
 * transaction — l'affichage ne fait pas foi.
 *
 * ----------------------------------------------------------------------------
 * Trois défauts corrigés le 10 septembre 2026
 * ----------------------------------------------------------------------------
 * 1. Le fichier était doublement encodé : « Année » s'affichait « AnnÃÂ©e ».
 * 2. `Toast` est un composant React, et le code appelait `Toast.error(...)` :
 *    chaque appel levait une TypeError, y compris dans le cas du succès.
 * 3. La page ne déclarait pas `onBackToDashboard` : c'était un cul-de-sac.
 *
 * Et le 12 septembre 2026, le quatrième, qui était le plus grave : l'aperçu des
 * trimestres était écrit en dur — 1er octobre, 31 décembre, 1er janvier... —
 * sans lire ni les dates saisies ni les vrais mois. Une école de septembre à
 * juillet voyait un calendrier qui ne correspondait à rien. Il est remplacé
 * par ce que la base contient réellement.
 */

// Les deux boutons d'un pied de modal ont la meme taille ET la meme largeur :
// un bouton d'annulation plus petit suggere une hierarchie qui n'existe pas.
// DESIGN-SYSTEM.md, section Boutons.
const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

const MOIS_VIDE = { id: null, nom: '', trimestre: 1, date_debut: '', date_fin: '' };

const ConfigurationCalendrierAnnuel = ({ onBackToDashboard }) => {
  // Le calendrier de paie decoupe l'annee en mois de paie ET en mensualites :
  // il ne sert qu'a une ecole qui a la comptabilite ou la paie. Les dates de
  // l'annee scolaire, elles, restent au socle (16 septembre 2026).
  const { estActive } = useFeatures();
  const calendrierPaieSousLicence = estActive('module.comptabilite') || estActive('module.paie');
  const [activeYear, setActiveYear] = useState(null);
  // En chaines 'AAAA-MM-JJ', comme en base. Comparer deux chaines ISO revient
  // a comparer deux dates, et rien ne traverse un objet Date ou un fuseau.
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  // Les bornes telles qu'elles sont en base, pour savoir ce qui a bouge et
  // pouvoir y revenir. Regime B : PARAMETRAGE-MODULES.md §5.
  const [bornesEnregistrees, setBornesEnregistrees] = useState({ debut: '', fin: '' });
  const [mois, setMois] = useState(null); // null = pas encore lu, [] = aucun mois
  const [chargement, setChargement] = useState(true);
  const [loading, setLoading] = useState(false);
  const [enregistrementMois, setEnregistrementMois] = useState(false);
  const [moisEnEdition, setMoisEnEdition] = useState(null);
  const [brouillon, setBrouillon] = useState(MOIS_VIDE);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const afficher = (message, type = 'success') => setToast({ show: true, message, type });

  useEffect(() => {
    (async () => {
      await loadCalendrier();
      setChargement(false);
    })();
  }, []);

  /**
   * L'année ET ses mois, en une seule lecture.
   *
   * Les bornes ne viennent PAS de `api.anneesScolaires.getActive()` : cette
   * fonction rend un objet fabriqué à partir du localStorage dès qu'un
   * `annee_scolaire_id` y est posé — c'est-à-dire toujours après connexion —
   * et cet objet n'a ni `date_debut` ni `date_fin`. Les deux calendriers
   * restaient donc vides en permanence, et la page ne pouvait qu'écraser des
   * bornes qu'elle était incapable d'afficher.
   *
   * Une lecture qui échoue laisse `mois` à `null`, jamais à `[]`. Un tableau
   * vide veut dire « cette année n'a aucun mois », ce qui est une alerte ;
   * une lecture ratée qui s'afficherait pareil serait un mensonge.
   */
  const loadCalendrier = async () => {
    try {
      const response = await api.moisScolaires.getCalendrier();
      if (response.success && response.data) {
        const annee = response.data.annee || null;
        setActiveYear(annee);
        setDateDebut(annee?.date_debut || '');
        setDateFin(annee?.date_fin || '');
        setBornesEnregistrees({ debut: annee?.date_debut || '', fin: annee?.date_fin || '' });
        setMois(response.data.mois || []);
      } else {
        setMois(null);
        afficher(response.message || "Le calendrier n'a pas pu être lu", 'error');
      }
    } catch (error) {
      console.error('Erreur chargement calendrier:', error);
      setMois(null);
      afficher("Le calendrier n'a pas pu être lu", 'error');
    }
  };

  const hasChanges =
    dateDebut !== bornesEnregistrees.debut || dateFin !== bornesEnregistrees.fin;

  const handleReset = () => {
    setDateDebut(bornesEnregistrees.debut);
    setDateFin(bornesEnregistrees.fin);
  };

  const handleSave = async () => {
    if (!dateDebut || !dateFin) {
      afficher('Les deux dates sont requises', 'error');
      return;
    }
    if (dateFin <= dateDebut) {
      afficher('La date de fin doit être après la date de début', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await api.anneesScolaires.update(activeYear.id, {
        debut: dateDebut,
        fin: dateFin
      });

      if (response.success) {
        afficher("Bornes de l'année scolaire mises à jour", 'success');
        await loadCalendrier();
      } else {
        afficher(response.message || 'Erreur lors de la mise à jour', 'error');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      afficher('Erreur lors de la sauvegarde', 'error');
    } finally {
      setLoading(false);
    }
  };

  const ouvrirEdition = (m) => {
    setMoisEnEdition(m);
    setBrouillon({
      id: m.id,
      nom: m.nom || '',
      trimestre: m.trimestre || 1,
      date_debut: m.date_debut || '',
      date_fin: m.date_fin || ''
    });
  };

  const enregistrerMois = async () => {
    if (!brouillon.nom.trim() || !brouillon.date_debut || !brouillon.date_fin) {
      afficher('Le nom et les deux dates sont requis', 'error');
      return;
    }
    if (brouillon.date_fin <= brouillon.date_debut) {
      afficher('La date de fin doit être après la date de début', 'error');
      return;
    }

    setEnregistrementMois(true);
    try {
      const response = await api.moisScolaires.update(brouillon.id, {
        nom: brouillon.nom.trim(),
        trimestre: Number(brouillon.trimestre),
        date_debut: brouillon.date_debut,
        date_fin: brouillon.date_fin
      });

      if (response.success) {
        afficher(response.message || 'Mois mis à jour', 'success');
        setMoisEnEdition(null);
        await loadCalendrier();
      } else {
        afficher(response.message || 'Le mois n\'a pas pu être modifié', 'error');
      }
    } catch (error) {
      console.error('Erreur enregistrement mois:', error);
      afficher('Le mois n\'a pas pu être modifié', 'error');
    } finally {
      setEnregistrementMois(false);
    }
  };

  /** Le nombre de semaines entre deux dates ISO, arrondi au superieur. */
  const semaines = (debut, fin) => {
    const jours = (new Date(`${fin}T12:00:00`) - new Date(`${debut}T12:00:00`)) / 86400000;
    return Number.isFinite(jours) ? Math.max(1, Math.ceil((jours + 1) / 7)) : '—';
  };

  /** Le nombre de jours couverts par un mois, bornes comprises. */
  const joursEntre = (debut, fin) => {
    const jours = (new Date(`${fin}T12:00:00`) - new Date(`${debut}T12:00:00`)) / 86400000;
    return Number.isFinite(jours) ? Math.round(jours) + 1 : '—';
  };

  const joliDate = (iso) => {
    if (!iso) return '—';
    try {
      return format(new Date(`${iso}T12:00:00`), 'dd MMM yyyy', { locale: fr });
    } catch {
      return iso;
    }
  };

  if (chargement) {
    return (
      <div className="w-full max-w-full mx-auto px-6">
        <EnTeteParametrage
          onRetour={onBackToDashboard}
          titre="Paramétrage du calendrier annuel"
          sousTitre="Bornes de l'année scolaire et calendrier de paie"
        />
        <ChargementSection accent="#319190" libelle="Chargement du calendrier…" />
      </div>
    );
  }

  const moisVerrouilles = Array.isArray(mois) ? mois.filter((m) => m.verrouille).length : 0;

  return (
    <div className="w-full">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      <EnTeteParametrage
        onRetour={onBackToDashboard}
        titre="Paramétrage du calendrier annuel"
        sousTitre="Bornes de l'année scolaire et calendrier de paie"
      />

      <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
        <div className="space-y-5">
          <EnTeteSection
            icone={Calendar}
            titre="Année scolaire en cours"
            sousTitre="Ces bornes servent à générer les emplois du temps annuels"
            accent="#319190"
            accentSombre="#2a7a79"
            actions={
              <>
                {activeYear?.nom && (
                  <span className="rounded-md bg-[#319190]/10 px-3 py-1.5 text-xs font-semibold text-[#319190]">
                    {activeYear.nom}
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={!hasChanges}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <RotateCcw size={13} /> Réinitialiser
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading || !activeYear || !hasChanges}
                  className={`flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${
                    hasChanges && activeYear
                      ? 'bg-[#319190] hover:shadow-md'
                      : 'cursor-not-allowed bg-gray-300 shadow-none'
                  }`}
                >
                  {loading
                    ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    : <Save size={13} />}
                  Enregistrer
                </button>
              </>
            }
          />

          <CarteReglages titre="Bornes pour les emplois du temps" accent="#319190">
            <p className="mb-4 text-xs leading-relaxed text-gray-500">
              Ces deux dates bornent la <strong>génération automatique des emplois du
              temps annuels</strong>, et rien d'autre. Elles ne décident pas du calendrier
              de paie — c'est le bloc ci-dessous qui s'en charge. Les repousser n'a donc
              aucun effet sur les salaires.
            </p>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Premier jour de l'année scolaire
                </label>
                <input
                  type="date"
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-[#319190]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dernier jour de l'année scolaire
                </label>
                <input
                  type="date"
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-[#319190]"
                />
              </div>
            </div>

            {dateDebut && dateFin && dateFin > dateDebut && (
              <p className="mt-3 text-xs text-gray-500">
                Du <span className="font-semibold text-gray-700">{joliDate(dateDebut)}</span>
                {' '}au <span className="font-semibold text-gray-700">{joliDate(dateFin)}</span>
                {' — '}
                <span className="font-semibold text-gray-700">{semaines(dateDebut, dateFin)} semaines</span>
                {' '}de créneaux générables.
              </p>
            )}

            {/* Le filet qui rattrape le changement d'onglet : la page est longue,
                l'en-tete de section est sorti du champ, et rien ne dirait
                autrement que les bornes saisies ne sont pas enregistrees. */}
            {hasChanges && (
              <div className="mt-4 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100">
                  <AlertCircle size={15} className="text-amber-600" />
                </div>
                <p className="text-xs font-semibold text-amber-700">
                  Vous avez des modifications non enregistrées.
                </p>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="ml-auto flex items-center gap-1.5 rounded-lg bg-amber-600 px-3.5 py-2 text-xs font-semibold text-white transition-all hover:bg-amber-700 disabled:opacity-60"
                >
                  <Save size={13} />
                  {loading ? 'Enregistrement…' : 'Enregistrer maintenant'}
                </button>
              </div>
            )}
          </CarteReglages>

          {/* ================================================================ */}
          {/* Le calendrier de paie, tel qu'il est en base                      */}
          {/* ================================================================ */}
          {calendrierPaieSousLicence && (
          <CarteReglages
            titre="Calendrier de paie"
            accent="#246990"
            compteur={
              Array.isArray(mois)
                ? `${mois.length} mois${moisVerrouilles > 0 ? ` · ${moisVerrouilles} figé(s)` : ''}`
                : 'non lu'
            }
          >
            <p className="mb-4 text-xs leading-relaxed text-gray-500">
              Ces mois ne sont pas créés ici : le CIK les pose à l'ouverture de l'école,
              la clôture annuelle les reprend pour l'année suivante. Cette page sert à les
              corriger. Un mois dont les salaires sont validés ou payés ne peut plus bouger —
              déplacer sa frontière changerait le mois de rattachement d'opérations déjà passées.
            </p>

            {mois === null && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                Le calendrier n'a pas pu être lu. Rien n'est affiché plutôt qu'un calendrier
                vide qui ferait croire qu'il n'y a aucun mois.
              </div>
            )}

            {Array.isArray(mois) && mois.length === 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-semibold text-red-900">
                  Cette année scolaire n'a aucun mois.
                </p>
                <p className="mt-1 text-xs leading-relaxed text-red-800">
                  Aucun salaire ne pourra être calculé tant que le calendrier n'est pas posé.
                  Il se crée à l'étape « calendrier » de la clôture annuelle, ou depuis le CIK
                  pour une école qui vient d'ouvrir.
                </p>
              </div>
            )}

            {Array.isArray(mois) && mois.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="w-16 px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                        #
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Mois
                      </th>
                      <th className="w-28 px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Trimestre
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Période
                      </th>
                      <th className="w-20 px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Durée
                      </th>
                      <th className="w-[190px] px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Correction
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {mois.map((m, index) => (
                      <tr
                        key={m.id}
                        className={`border-b border-gray-100 last:border-b-0 transition-colors hover:bg-gray-50/50 ${
                          m.est_courant ? 'bg-[#246990]/5' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                        }`}
                      >
                        <td className="px-5 py-3.5 text-center">
                          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
                            {m.ordre}
                          </span>
                        </td>

                        <td className="px-5 py-3.5">
                          <span className="text-sm font-medium text-gray-900">{m.nom}</span>
                          {m.est_courant && (
                            <span className="ml-2 rounded-full bg-[#246990] px-2 py-0.5 text-[10px] font-semibold text-white">
                              en cours
                            </span>
                          )}
                        </td>

                        <td className="px-5 py-3.5 text-center text-sm text-gray-600">
                          {m.trimestre}
                        </td>

                        <td className="px-5 py-3.5 text-sm text-gray-600">
                          {joliDate(m.date_debut)} → {joliDate(m.date_fin)}
                        </td>

                        <td className="px-5 py-3.5 text-right text-sm text-gray-600">
                          {m.duree_jours} j
                        </td>

                        <td className="px-5 py-3.5 text-right">
                          {m.verrouille ? (
                            <span
                              className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600"
                              title={m.motif_verrou}
                            >
                              <Lock size={12} /> Figé
                            </span>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-[150px] justify-center gap-2"
                              onClick={() => ouvrirEdition(m)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              Corriger
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CarteReglages>
          )}

          {/* Encadré d'information — norme §8 */}
          {/* Deux calendriers seulement quand l'ecole a la comptabilite ou la paie :
              sans elles, il n'y a plus que les bornes de l'annee. */}
          {calendrierPaieSousLicence && (
          <div className="rounded-xl border border-[#246990]/20 bg-[#eef7fb] p-4">
            <div className="flex gap-3">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#246990]/20 bg-white shadow-sm">
                <AlertTriangle className="h-4 w-4 text-[#246990]" />
              </div>
              <div className="text-xs leading-relaxed text-gray-600">
                <p className="mb-1 text-[13px] font-semibold text-[#246990]">
                  Deux calendriers, deux usages
                </p>
                <p>
                  Les <strong>bornes de l'année</strong> servent à générer les emplois du temps.
                  Le <strong>calendrier de paie</strong> décide à quel mois appartiennent un
                  pointage ou un paiement. Les deux n'ont aucune raison de coïncider au jour
                  près : une école peut faire sa rentrée le 6 octobre et compter son mois
                  d'octobre à partir du 1er.
                </p>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* ================================================================== */}
      {/* Correction d'un mois                                               */}
      {/* ================================================================== */}
      <Dialog open={Boolean(moisEnEdition)} onOpenChange={(ouvert) => !ouvert && setMoisEnEdition(null)}>
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <CalendarClock size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Corriger {moisEnEdition?.nom}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Position {moisEnEdition?.ordre} dans l'année de paie — elle ne change pas ici
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <div className="md:col-span-3">
                <label className="mb-2 block text-sm text-gray-700">Nom du mois</label>
                <input
                  type="text"
                  value={brouillon.nom}
                  onChange={(e) => setBrouillon((p) => ({ ...p, nom: e.target.value }))}
                  className="h-9 w-full rounded-lg border border-gray-200 px-3 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm text-gray-700">Trimestre</label>
                <input
                  type="number"
                  min="1"
                  max="6"
                  value={brouillon.trimestre}
                  onChange={(e) => setBrouillon((p) => ({ ...p, trimestre: e.target.value }))}
                  className="h-9 w-full rounded-lg border border-gray-200 px-3 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm text-gray-700">Premier jour</label>
                <input
                  type="date"
                  value={brouillon.date_debut}
                  onChange={(e) => setBrouillon((p) => ({ ...p, date_debut: e.target.value }))}
                  className="h-9 w-full rounded-lg border border-gray-200 px-3 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
                <p className="mt-1 text-[11px] text-gray-500">C'est le jour 1 du mois de paie</p>
              </div>
              <div>
                <label className="mb-2 block text-sm text-gray-700">Dernier jour</label>
                <input
                  type="date"
                  value={brouillon.date_fin}
                  onChange={(e) => setBrouillon((p) => ({ ...p, date_fin: e.target.value }))}
                  className="h-9 w-full rounded-lg border border-gray-200 px-3 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                />
                {brouillon.date_debut && brouillon.date_fin && brouillon.date_fin > brouillon.date_debut && (
                  <p className="mt-1 text-[11px] text-gray-500">
                    {joursEntre(brouillon.date_debut, brouillon.date_fin)} jours de paie
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-[#246990]/20 bg-[#eef7fb] p-4">
              <div className="flex gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#246990]/20 bg-white shadow-sm">
                  <AlertTriangle className="h-4 w-4 text-[#246990]" />
                </div>
                <p className="text-xs leading-relaxed text-gray-600">
                  Les dates ne doivent chevaucher aucun autre mois de l'année : un jour compté
                  dans deux mois de paie serait payé deux fois. L'enregistrement le vérifie et
                  nomme le mois en conflit s'il y en a un.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED_CLASSES}
              onClick={() => setMoisEnEdition(null)}
              disabled={enregistrementMois}
            >
              Annuler
            </Button>
            <Button
              className={`${BOUTON_PIED_CLASSES} bg-[#246990] text-white hover:bg-[#1f5a7a]`}
              onClick={enregistrerMois}
              disabled={enregistrementMois}
            >
              <Save className="h-4 w-4" />
              {enregistrementMois ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConfigurationCalendrierAnnuel;